/**
 * WEBSOCKET / SIGNALR ADMIN SERVICE: ARMA LA PLAYLIST [HU-04]
 * Wrapper de comunicación bidireccional en tiempo real para la consola de DJ / Operador de Cabina y Administración.
 * Conecta con el Hub de SignalR para recibir sugerencias en tiempo real, votos acumulados,
 * actualizaciones de Now Playing, cambios en la cola de reproducción, ajustes y reinicios de dinámica.
 * 
 * @module utilities/WS/Admin/PlaylistWS
 */

import SignalRService from '../SignalRService';

/**
 * Referencia al handler registrado para mensajes generales en SignalRService.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Lista de eventos soportados por Arma la Playlist para normalización y enrutamiento.
 */
export const PLAYLIST_WS_EVENTS = [
    'suggestion_received',
    'vote_added',
    'suggestion_updated',
    'suggestion_archived',
    'suggestion_deleted',
    'playlist_updated',
    'now_playing_changed',
    'metrics_updated',
    'settings_updated',
    'dynamic_reset'
];

/**
 * Conecta la consola de administración / cabina de sonido al Hub de SignalR.
 * 
 * Eventos en tiempo real procesados:
 * - 'suggestion_received' / 'playlist:suggestion_received': Nueva sugerencia enviada por aficionado.
 * - 'vote_added' / 'playlist:vote_added': Nuevo voto registrado / incremento de contador.
 * - 'suggestion_updated' / 'playlist:suggestion_updated': Modificación de título, artista o estado de sugerencia.
 * - 'suggestion_archived' / 'playlist:suggestion_archived': Canción archivada o desarchivada.
 * - 'suggestion_deleted' / 'playlist:suggestion_deleted': Canción eliminada.
 * - 'playlist_updated' / 'playlist:playlist_updated': Reordenamiento o actualización de la cola activa.
 * - 'now_playing_changed' / 'playlist:now_playing_changed': Cambio de canción en reproducción en vivo.
 * - 'metrics_updated' / 'playlist:metrics_updated': Actualización de métricas y estadísticas.
 * - 'settings_updated' / 'playlist:settings_updated': Actualización de configuración visual u operativa.
 * - 'dynamic_reset' / 'playlist:dynamic_reset': Notificación de reinicio de la dinámica.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback para despachar eventos al componente React.
 * @param {Function} [onStatusChange=() => {}] - Callback de estado ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [roomId=''] - GUID de la sala para filtrar el tráfico en tiempo real.
 */
export const ConnectWebSocketForPlaylistAdmin = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = ''
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria y duplicaciones
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // Extraer roomId del envelope o de su carga útil
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        // Normalizar el nombre del tipo de evento (quitar prefijo 'playlist:' si existe)
        const rawType = messageEnvelope.type || '';
        const normalizedType = rawType.startsWith('playlist:') ? rawType.replace(/^playlist:/, '') : rawType;

        const normalizedEnvelope = {
            ...messageEnvelope,
            rawType: rawType,
            type: normalizedType,
            payload: messageEnvelope.payload !== undefined ? messageEnvelope.payload : messageEnvelope.data,
            data: messageEnvelope.data !== undefined ? messageEnvelope.data : messageEnvelope.payload
        };

        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(normalizedEnvelope);
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    // Registrar escuchas en SignalRService
    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    // Iniciar la conexión SignalR con los metadatos de operador de Playlist
    SignalRService.connect({
        tipo: 'playlist:admin',
        roomId: roomId || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[PlaylistWS] Error al conectar con SignalR Hub:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión de SignalR del operador de Playlist y libera todos los handlers registrados.
 */
export const DisconnectWebSocketForPlaylistAdmin = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[PlaylistWS] Error al desconectar la sesión de SignalR:', disconnectionError);
    }
};

/**
 * Conecta y suscribe handlers reactivos para la pantalla de estadio o componentes de Playlist.
 * 
 * @param {string} roomId - GUID de la sala.
 * @param {Object|Function} handlers - Diccionario de callbacks por tipo de evento o callback único.
 * @param {Function} [onStatusChange=() => {}] - Callback de estado.
 */
export const connect = (roomId, handlers = {}, onStatusChange = () => {}) => {
    const onIncoming = (envelope) => {
        if (!envelope) return;
        const rawType = envelope.rawType || envelope.type || '';
        const normalizedType = envelope.type || rawType.replace(/^playlist:/, '');
        const payload = envelope.payload !== undefined ? envelope.payload : envelope.data;

        if (typeof handlers === 'function') {
            handlers(envelope);
            return;
        }

        if (handlers && typeof handlers === 'object') {
            if (typeof handlers[normalizedType] === 'function') {
                handlers[normalizedType](payload, envelope);
            } else if (typeof handlers[rawType] === 'function') {
                handlers[rawType](payload, envelope);
            }

            if (typeof handlers.onMessage === 'function') {
                handlers.onMessage(envelope);
            }
        }
    };

    ConnectWebSocketForPlaylistAdmin(onIncoming, onStatusChange, roomId);
};

export const disconnect = DisconnectWebSocketForPlaylistAdmin;

export const PlaylistWS = {
    PLAYLIST_WS_EVENTS,
    ConnectWebSocketForPlaylistAdmin,
    DisconnectWebSocketForPlaylistAdmin,
    connect,
    disconnect
};

export default {
    PLAYLIST_WS_EVENTS,
    ConnectWebSocketForPlaylistAdmin,
    DisconnectWebSocketForPlaylistAdmin,
    connect,
    disconnect
};
